import { clearDashboardPersistence } from '@/features/business-dashboard/hooks/usePersistentState';
import { resetDataOriginStore } from '@/features/business-dashboard/hooks/useDataOrigin';
import { API_BASE_URL } from '../client';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user_id: number;
  user_name: string;
  user_role: string;
  feature_access_ids?: number[];
}

export interface User {
  id: number;
  name: string;
  user_name: string;
  email: string;
  role: string;
  status: string;
  feature_access_ids?: number[];
  attendance_marked_today?: boolean;
}

const SSO_TOKEN_TTL_MS = 60 * 60 * 1000; // 1 hour (no refresh token)
export const AUTH_FLASH_KEY = 'auth_flash';
export const AUTH_FLASH_SESSION_EXPIRED = 'session_expired';

export class AuthService {
  private static handlingUnauthorized = false;

  private static getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }

  /** Clear auth keys from localStorage without calling the logout API. */
  static clearLocalSession(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_role');
    localStorage.removeItem('token_expires_at');
    localStorage.removeItem('feature_access_ids');
  }

  /**
   * Persist an SSO token from the Prosparity 2W app (#token=jwt).
   * Role / feature_access_ids come from GET /users/me — never from the JWT.
   */
  static acceptSsoToken(token: string): void {
    localStorage.setItem('access_token', token);
    localStorage.setItem('token_expires_at', String(Date.now() + SSO_TOKEN_TTL_MS));
    // Drop stale profile until /users/me hydrates
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_role');
    localStorage.removeItem('feature_access_ids');
  }

  /**
   * Read #token= from the URL hash, store it, and strip the fragment.
   * Must run before the router / auth gate mounts to avoid a login flash.
   */
  static consumeSsoTokenFromHash(): void {
    const rawHash = window.location.hash;
    if (!rawHash || !rawHash.includes('token=')) {
      return;
    }

    const params = new URLSearchParams(
      rawHash.startsWith('#') ? rawHash.slice(1) : rawHash
    );
    const token = params.get('token')?.trim();
    if (!token) {
      return;
    }

    this.acceptSsoToken(token);
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }

  static markSessionExpired(): void {
    sessionStorage.setItem(AUTH_FLASH_KEY, AUTH_FLASH_SESSION_EXPIRED);
  }

  static consumeAuthFlash(): string | null {
    const flash = sessionStorage.getItem(AUTH_FLASH_KEY);
    if (flash) {
      sessionStorage.removeItem(AUTH_FLASH_KEY);
    }
    return flash;
  }

  /**
   * Global unauthorized handler: clear session and send user to login.
   * Safe to call multiple times (no redirect loops on /login).
   */
  static handleUnauthorized(): void {
    if (this.handlingUnauthorized) {
      return;
    }
    this.handlingUnauthorized = true;
    this.clearLocalSession();
    this.markSessionExpired();

    const onLoginPage = window.location.pathname.startsWith('/login');
    if (!onLoginPage) {
      window.location.replace('/login');
    } else {
      this.handlingUnauthorized = false;
    }
  }

  static async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const body = new URLSearchParams();
    body.append('username', credentials.email);
    body.append('password', credentials.password);

    const response = await fetch(`${API_BASE_URL}/users/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });

    if (!response.ok) {
      let message = 'Invalid email or password.';
      try {
        const errorData = await response.json();
        const detail = errorData?.detail ?? errorData?.message ?? errorData?.error;
        if (typeof detail === 'string' && detail.trim()) {
          message = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          message = detail
            .map((err: { msg?: string } | string) =>
              typeof err === 'string' ? err : err?.msg || 'Invalid email or password.'
            )
            .join('; ');
        }
      } catch {
        if (response.status !== 400 && response.status !== 401 && response.status !== 422) {
          message = 'Login failed. Please try again.';
        }
      }
      throw new Error(message);
    }

    const authData: AuthResponse = await response.json();

    // Drop previous tab's dashboard filters before binding a new session.
    // sessionStorage survives SPA logout/login and was previously shared across users.
    clearDashboardPersistence();
    resetDataOriginStore();
    
    // Store token and user info
    localStorage.setItem('access_token', authData.access_token);
    localStorage.setItem('user_id', authData.user_id.toString());
    localStorage.setItem('user_name', authData.user_name);
    localStorage.setItem('user_role', authData.user_role);
    localStorage.setItem('token_expires_at', (Date.now() + authData.expires_in * 1000).toString());
    
    // Store feature_access_ids if present
    if (authData.feature_access_ids) {
      localStorage.setItem('feature_access_ids', JSON.stringify(authData.feature_access_ids));
    } else {
      localStorage.setItem('feature_access_ids', JSON.stringify([]));
    }

    return authData;
  }

  static async logout(): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/users/logout`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear auth + dashboard UI persistence (filters live in sessionStorage).
      clearDashboardPersistence();
      resetDataOriginStore();
      this.clearLocalSession();
    }
  }

  static async getCurrentUser(): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/me`, {
      headers: this.getAuthHeaders(),
    });

    if (response.status === 401 || response.status === 403) {
      throw new Error('UNAUTHORIZED');
    }

    if (!response.ok) {
      throw new Error('Failed to get current user');
    }

    const userData = await response.json();
    
    // Map backend response to frontend User interface
    // Role and feature_access_ids always come from the API (DB), never from JWT claims
    const featureAccessIds = userData.feature_access_ids || [];
    const userName = userData.user_name || userData.name || 'User';
    const userRole = userData.role || '';

    localStorage.setItem('user_id', String(userData.id));
    localStorage.setItem('user_name', userName);
    localStorage.setItem('user_role', userRole);
    localStorage.setItem('feature_access_ids', JSON.stringify(featureAccessIds));
    
    const user: User = {
      id: userData.id,
      name: userData.name || '',
      user_name: userName,
      email: userData.email || '',
      role: userRole,
      status: userData.status || 'active',
      feature_access_ids: featureAccessIds,
      attendance_marked_today: userData.attendance_marked_today
    };

    return user;
  }

  static isAuthenticated(): boolean {
    const token = localStorage.getItem('access_token');
    const expiresAt = localStorage.getItem('token_expires_at');
    
    if (!token || !expiresAt) {
      return false;
    }

    // Check if token is expired
    if (Date.now() > parseInt(expiresAt)) {
      this.clearLocalSession();
      this.markSessionExpired();
      return false;
    }

    return true;
  }

  static getCurrentUserRole(): string | null {
    return localStorage.getItem('user_role');
  }

  static getCurrentUserName(): string | null {
    return localStorage.getItem('user_name');
  }

  static getCurrentUserId(): number | null {
    const id = localStorage.getItem('user_id');
    return id ? parseInt(id) : null;
  }

  static isAdmin(): boolean {
    const role = (this.getCurrentUserRole() || '').trim().toLowerCase();
    return role === 'admin' || role === 'superadmin';
  }

  static canReviewChargesWaiver(): boolean {
    return this.isAdmin();
  }

  static isRM(): boolean {
    const role = this.getCurrentUserRole();
    return role === 'RM';
  }

  static isTL(): boolean {
    const role = this.getCurrentUserRole();
    return role === 'TL';
  }
  
  static isBE(): boolean {
    const role = this.getCurrentUserRole();
    return role === 'BE';
  }

  static canViewPendingApprovals(): boolean {
    return this.isAdmin() || this.isBE() || this.isTL() || this.isRM();
  }

  static getFeatureAccessIds(): number[] {
    const stored = localStorage.getItem('feature_access_ids');
    if (!stored) return [];
    try {
      return JSON.parse(stored);
    } catch {
      return [];
    }
  }

  static hasFeatureAccess(profileId: number): boolean {
    const featureAccessIds = this.getFeatureAccessIds();
    return featureAccessIds.includes(profileId);
  }
}
